import { useState, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useFetchProductRequests } from '../../hooks/useFetchProductRequests'
import { ProductsListSkeleton } from '../../ui/ProductsListSkelton'
import { ProductRequestCard } from '../../ui/ProductRequestCard'

const ITEMS_PER_PAGE = 20

export const ProductRequests = () => {
  const { productRequests, isError, isLoading, error, pagination } =
    useFetchProductRequests({ page: 1, per_page: ITEMS_PER_PAGE })
  const [allProductRequests, setAllProductRequests] = useState<
    typeof productRequests
  >([])
  const [hasMore, setHasMore] = useState(true)
  const observerTarget = useRef<HTMLDivElement>(null)

  // Initialize product requests on first load
  useEffect(() => {
    if (productRequests && pagination.page === 1) {
      setAllProductRequests(productRequests)
      setHasMore(productRequests.length === ITEMS_PER_PAGE)
    }
  }, [productRequests, pagination.page])

  // Append product requests when loading subsequent pages
  useEffect(() => {
    if (productRequests && pagination.page > 1) {
      setAllProductRequests((prev) => {
        if (!prev) return productRequests
        // Deduplicate by ID to prevent duplicate keys
        const existingIds = new Set(prev.map((item) => item.id))
        const newItems = productRequests.filter(
          (item) => !existingIds.has(item.id),
        )
        return [...prev, ...newItems]
      })
      setHasMore(productRequests.length === ITEMS_PER_PAGE)
    }
  }, [productRequests, pagination.page])

  // Setup Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          pagination.goToPage(pagination.page + 1)
        }
      },
      { threshold: 0.1 },
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current)
      }
    }
  }, [hasMore, isLoading, pagination.page])

  return (
    <div className='my-10 container'>
      <Helmet>
        <title className='text-xl font-semibold'>کتاب های درخواستی</title>
        <meta
          name='description'
          content='بوک استوری - هر کتاب یک داستان، هر داستان یک زندگی دوباره'
        />
      </Helmet>
      <h1
        className='my-4 text-xl font-bold text-white p-5 rounded-md'
        style={{ backgroundColor: 'var(--accent-secondary)' }}
      >
        کتاب های درخواستی
      </h1>
      {isError && <h3 className='text-center my-4 w-full'>{error?.message}</h3>}
      {isLoading && pagination.page === 1 && <ProductsListSkeleton />}

      <div
        className='mt-8 p-4 rounded-md border'
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--border-color)',
        }}
      >
        {allProductRequests && allProductRequests?.length > 0 ? (
          <>
            <div className='grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mt-3'>
              {allProductRequests.map((request) => (
                <ProductRequestCard
                  key={request.id}
                  id={request.id}
                  productName={request.productName}
                  author={request.author}
                  publisher={request.publisher}
                  translator={request.translator}
                  imageUrl={null}
                />
              ))}
            </div>

            {/* Sentinel element for infinite scroll */}
            {hasMore && (
              <div
                ref={observerTarget}
                className='h-10 w-full flex justify-center items-center mt-4'
              >
                {isLoading && pagination.page > 1 && (
                  <div className='text-sm text-gray-500'>
                    در حال بارگذاری...
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          !isLoading && (
            <p className='px-4 text-gray-600 dark:text-gray-300 text-center'>
              هیچ درخواست کتابی ثبت نشده است.
            </p>
          )
        )}
      </div>
    </div>
  )
}
